import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Download, Send, Trash2 } from 'lucide-react';
import Toast from '../../components/Toast';
import { useToast } from '../../hooks/useToast';
import apiService from '../../services/api';
import {
  NR2J_VAT_OPTIONS,
  centsToEuros,
  computeQuoteLineTotals,
  eurosToCents,
  formatCentsAsEuros,
  formatNr2jDate,
  nr2jCreditNoteStatusBadge,
  sumQuoteTotals,
} from '../../utils/nr2jConstants';

function lineFromApi(line) {
  return {
    key: line.id || `${Date.now()}-${Math.random()}`,
    invoiceLineId: line.invoiceLineId || '',
    name: line.name || '',
    description: line.description || '',
    quantity: line.quantity || 1,
    unitPriceHtCents: line.unitPriceHtCents || 0,
    priceEuros: centsToEuros(line.unitPriceHtCents),
    vatRateBps: line.vatRateBps ?? 2000,
    billingType: line.billingType || 'ONE_TIME',
    discountPercentBps: line.discountPercentBps ?? null,
    discountAmountHtCents: line.discountAmountHtCents ?? null,
  };
}

function lineToPayload(line, index) {
  const unitPriceHtCents = line.priceEuros !== ''
    ? eurosToCents(line.priceEuros)
    : Number(line.unitPriceHtCents) || 0;
  return {
    invoiceLineId: line.invoiceLineId || null,
    catalogItemId: null,
    sortOrder: index,
    name: line.name.trim(),
    description: line.description?.trim() || null,
    quantity: Math.max(1, Number(line.quantity) || 1),
    unitPriceHtCents,
    vatRateBps: Number(line.vatRateBps),
    billingType: line.billingType || 'ONE_TIME',
    discountPercentBps: line.discountPercentBps,
    discountAmountHtCents: line.discountAmountHtCents,
  };
}

export default function Nr2jCreditNoteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toasts, toast, removeToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [creditNote, setCreditNote] = useState(null);
  const [lines, setLines] = useState([]);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const isDraft = creditNote?.status === 'DRAFT';
  const badge = nr2jCreditNoteStatusBadge(creditNote?.status);
  const totals = useMemo(
    () => sumQuoteTotals(lines.map((l) => lineToPayload(l, 0))),
    [lines],
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.getNr2jCreditNote(id);
      const cn = res?.creditNote || res;
      setCreditNote(cn);
      setLines((cn.lines || []).map(lineFromApi));
      setReason(cn.reason || '');
    } catch (error) {
      toast.error(error?.message || 'Avoir introuvable');
      navigate('/nr2j/facturation');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  async function saveDraft() {
    if (!lines.length) {
      toast.error('Au moins une ligne est requise');
      return;
    }
    setBusy(true);
    try {
      const res = await apiService.patchNr2jCreditNote(id, {
        reason: reason || null,
        lines: lines.map(lineToPayload),
      });
      const cn = res?.creditNote || res;
      setCreditNote(cn);
      setLines((cn.lines || []).map(lineFromApi));
      toast.success('Avoir enregistré');
    } catch (error) {
      toast.error(error?.message || 'Enregistrement impossible');
    } finally {
      setBusy(false);
    }
  }

  async function issue() {
    setBusy(true);
    try {
      if (isDraft) {
        await apiService.patchNr2jCreditNote(id, {
          reason: reason || null,
          lines: lines.map(lineToPayload),
        });
      }
      const res = await apiService.issueNr2jCreditNote(id);
      setCreditNote(res?.creditNote || res);
      toast.success('Avoir émis');
      await load();
    } catch (error) {
      toast.error(error?.message || 'Émission impossible');
    } finally {
      setBusy(false);
    }
  }

  async function downloadPdf() {
    setBusy(true);
    try {
      await apiService.downloadNr2jCreditNotePdf(id, { number: creditNote?.number });
    } catch (error) {
      toast.error(error?.message || 'PDF indisponible');
    } finally {
      setBusy(false);
    }
  }

  async function deleteDraft() {
    if (!window.confirm('Supprimer cet avoir brouillon ?')) return;
    setBusy(true);
    try {
      await apiService.deleteNr2jCreditNote(id);
      toast.success('Avoir supprimé');
      if (creditNote?.invoiceId) {
        navigate(`/nr2j/facturation/${creditNote.invoiceId}`);
      } else {
        navigate('/nr2j/facturation');
      }
    } catch (error) {
      toast.error(error?.message || 'Suppression impossible');
    } finally {
      setBusy(false);
    }
  }

  if (loading || !creditNote) {
    return (
      <div className="p-8 text-sm text-gray-500" data-testid="nr2j-credit-note-loading">
        Chargement de l’avoir…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="nr2j-credit-note-detail">
      <div className="sticky top-0 z-20 -mx-1 flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 bg-white/95 px-1 py-3 backdrop-blur">
        <div>
          {creditNote.invoiceId ? (
            <Link
              to={`/nr2j/facturation/${creditNote.invoiceId}`}
              className="inline-flex items-center gap-1 text-sm text-indigo-700"
            >
              <ArrowLeft className="h-4 w-4" /> Retour à la facture
            </Link>
          ) : (
            <Link to="/nr2j/facturation" className="inline-flex items-center gap-1 text-sm text-indigo-700">
              <ArrowLeft className="h-4 w-4" /> Retour à la facturation
            </Link>
          )}
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <h1 className="text-xl font-bold text-gray-900 sm:text-2xl">
              {creditNote.number || 'Avoir brouillon'}
            </h1>
            <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium ${badge.className}`}>
              {badge.label}
            </span>
          </div>
          {creditNote.invoiceNumber ? (
            <p className="text-sm text-gray-600">
              Facture {creditNote.invoiceNumber}
            </p>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-2">
          {isDraft ? (
            <>
              <button
                type="button"
                disabled={busy}
                onClick={saveDraft}
                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium"
              >
                Enregistrer
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={downloadPdf}
                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
              >
                <Download className="mr-1 inline h-4 w-4" /> Aperçu PDF
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={issue}
                className="inline-flex items-center gap-1 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white"
              >
                <Send className="h-4 w-4" /> Émettre
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={deleteDraft}
                className="inline-flex items-center gap-1 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800"
              >
                <Trash2 className="h-4 w-4" /> Supprimer
              </button>
            </>
          ) : (
            <button
              type="button"
              disabled={busy}
              onClick={downloadPdf}
              className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
            >
              <Download className="h-4 w-4" /> PDF
            </button>
          )}
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-3">
        <div className="space-y-6 xl:col-span-2">
          <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <label className="block text-sm">
              <span className="text-gray-500">Motif</span>
              <textarea
                rows={2}
                disabled={!isDraft}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 disabled:bg-gray-50"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </label>
          </section>

          <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Lignes</h2>
            <div className="mt-4 space-y-4">
              {lines.map((line, idx) => {
                const lineTotals = computeQuoteLineTotals(lineToPayload(line, 0));
                return (
                  <div key={line.key} className="rounded-lg border border-gray-100 p-3">
                    {isDraft ? (
                      <div className="grid gap-2 sm:grid-cols-2">
                        <label className="block text-sm sm:col-span-2">
                          <span className="text-gray-500">Désignation</span>
                          <input
                            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                            value={line.name}
                            onChange={(e) => setLines((prev) => prev.map((l, i) => (
                              i === idx ? { ...l, name: e.target.value } : l
                            )))}
                          />
                        </label>
                        <label className="block text-sm">
                          <span className="text-gray-500">Qté</span>
                          <input
                            type="number"
                            min="1"
                            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                            value={line.quantity}
                            onChange={(e) => setLines((prev) => prev.map((l, i) => (
                              i === idx ? { ...l, quantity: e.target.value } : l
                            )))}
                          />
                        </label>
                        <label className="block text-sm">
                          <span className="text-gray-500">Prix unitaire HT (€)</span>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                            value={line.priceEuros}
                            onChange={(e) => setLines((prev) => prev.map((l, i) => (
                              i === idx ? { ...l, priceEuros: e.target.value } : l
                            )))}
                          />
                        </label>
                        <label className="block text-sm">
                          <span className="text-gray-500">TVA</span>
                          <select
                            className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                            value={line.vatRateBps}
                            onChange={(e) => setLines((prev) => prev.map((l, i) => (
                              i === idx ? { ...l, vatRateBps: Number(e.target.value) } : l
                            )))}
                          >
                            {NR2J_VAT_OPTIONS.map((v) => (
                              <option key={v.bps} value={v.bps}>{v.label}</option>
                            ))}
                          </select>
                        </label>
                      </div>
                    ) : (
                      <div>
                        <p className="font-medium text-gray-900">{line.name}</p>
                        <p className="text-xs text-gray-500">
                          Qté {line.quantity} · {centsToEuros(line.unitPriceHtCents)} € HT
                        </p>
                      </div>
                    )}
                    <p className="mt-2 text-xs text-gray-500">
                      HT {formatCentsAsEuros(lineTotals.lineHtCents)} € · TTC{' '}
                      {formatCentsAsEuros(lineTotals.lineTtcCents)} €
                    </p>
                    {isDraft && lines.length > 1 ? (
                      <button
                        type="button"
                        className="mt-1 text-xs text-rose-700 hover:underline"
                        onClick={() => setLines((prev) => prev.filter((_, i) => i !== idx))}
                      >
                        Supprimer la ligne
                      </button>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </section>
        </div>

        <aside>
          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm text-sm">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Totaux</h2>
            <dl className="mt-3 space-y-2">
              <div className="flex justify-between">
                <dt className="text-gray-500">Total HT</dt>
                <dd className="font-medium">{formatCentsAsEuros(totals.totalHtCents)} €</dd>
              </div>
              <div className="flex justify-between border-t border-gray-100 pt-2">
                <dt className="font-semibold text-gray-900">Total TTC</dt>
                <dd className="font-bold text-gray-900">{formatCentsAsEuros(totals.totalTtcCents)} €</dd>
              </div>
              {creditNote.issuedAt ? (
                <div className="flex justify-between pt-2 text-xs text-gray-500">
                  <dt>Émis le</dt>
                  <dd>{formatNr2jDate(creditNote.issuedAt)}</dd>
                </div>
              ) : null}
            </dl>
          </div>
        </aside>
      </div>

      {toasts.map((item) => (
        <Toast key={item.id} toast={item} onClose={() => removeToast(item.id)} />
      ))}
    </div>
  );
}
